import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ClearIcon from '@mui/icons-material/Clear';
import {
    Button,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Input } from '..';

const CUSTOM_RANGE = 'custom';

const blackDatePickerTheme = createTheme({
    palette: {
        primary: {
            main: '#000000',
        },
    },
});

const withNoon = (date) => date.clone().hour(12);

const getPresetOptions = (maxDate) => [
    {
        label: 'Today',
        value: 'today',
        startDate: withNoon(maxDate),
        endDate: withNoon(maxDate),
    },
    {
        label: 'Yesterday',
        value: 'yesterday',
        startDate: withNoon(maxDate).subtract(1, 'day'),
        endDate: withNoon(maxDate).subtract(1, 'day'),
    },
    {
        label: 'Last 7 Days',
        value: 'last_7_days',
        startDate: withNoon(maxDate).subtract(6, 'days'),
        endDate: withNoon(maxDate),
    },
    {
        label: 'Last 30 Days',
        value: 'last_30_days',
        startDate: withNoon(maxDate).subtract(29, 'days'),
        endDate: withNoon(maxDate),
    },
    {
        label: 'Month to Date',
        value: 'month_to_date',
        startDate: withNoon(maxDate).startOf('month').hour(12),
        endDate: withNoon(maxDate),
    },
    {
        label: 'Year to Date',
        value: 'year_to_date',
        startDate: withNoon(maxDate).startOf('year').hour(12),
        endDate: withNoon(maxDate),
    },
    {
        label: 'Custom Range',
        value: CUSTOM_RANGE,
        startDate: null,
        endDate: null,
    },
];

const getMatchingPresetValue = (startDate, endDate, presetOptions) => {
    if (!startDate || !endDate) {
        return CUSTOM_RANGE;
    }

    const matchedPreset = presetOptions.find((preset) => (
        preset.value !== CUSTOM_RANGE
        && preset.startDate.isSame(startDate, 'day')
        && preset.endDate.isSame(endDate, 'day')
    ));

    return matchedPreset?.value || CUSTOM_RANGE;
};

function DateRangeFields({
    initialEndDate,
    initialStartDate,
    maxDate,
    minStartDate,
    onChangeDateRange,
    startLabel,
    endLabel,
}) {
    const matchUpLg = useMediaQuery(blackDatePickerTheme.breakpoints.up('lg'));
    const presetOptions = useMemo(() => getPresetOptions(maxDate), [maxDate]);
    const [appliedStartDate, setAppliedStartDate] = useState(initialStartDate);
    const [appliedEndDate, setAppliedEndDate] = useState(initialEndDate);
    const [selectedPreset, setSelectedPreset] = useState(() => (
        getMatchingPresetValue(initialStartDate, initialEndDate, presetOptions)
    ));
    const [draftStartDate, setDraftStartDate] = useState(initialStartDate);
    const [draftEndDate, setDraftEndDate] = useState(initialEndDate);

    useEffect(() => {
        setAppliedStartDate(initialStartDate);
        setDraftStartDate(initialStartDate);
        setSelectedPreset(getMatchingPresetValue(initialStartDate, initialEndDate, presetOptions));
    }, [initialStartDate, initialEndDate, presetOptions]);

    useEffect(() => {
        setAppliedEndDate(initialEndDate);
        setDraftEndDate(initialEndDate);
    }, [initialEndDate]);

    useEffect(() => {
        if (!draftStartDate && draftEndDate) {
            setDraftEndDate(null);
        }
    }, [draftEndDate, draftStartDate]);

    const onChangeStartDate = (nextStartDate) => {
        if (!nextStartDate || !nextStartDate.isValid()) {
            return;
        }

        setDraftStartDate(nextStartDate);
        setDraftEndDate(nextStartDate);
    };

    const onChangeEndDate = (nextEndDate) => {
        if (!nextEndDate || !nextEndDate.isValid() || !draftStartDate) {
            return;
        }

        setDraftEndDate(nextEndDate);
        setSelectedPreset(CUSTOM_RANGE);
    };

    const onChangePreset = (event) => {
        const nextPreset = event.target.value;
        setSelectedPreset(nextPreset);

        if (nextPreset === CUSTOM_RANGE) {
            return;
        }

        const selectedOption = presetOptions.find((preset) => preset.value === nextPreset);

        if (!selectedOption) {
            return;
        }

        setDraftStartDate(selectedOption.startDate);
        setDraftEndDate(selectedOption.endDate);
        setAppliedStartDate(selectedOption.startDate);
        setAppliedEndDate(selectedOption.endDate);
        onChangeDateRange(selectedOption.startDate, selectedOption.endDate);
    };

    const applyCustomDateRange = () => {
        if (!draftStartDate || !draftEndDate) {
            return;
        }

        setAppliedStartDate(draftStartDate);
        setAppliedEndDate(draftEndDate);
        onChangeDateRange(draftStartDate, draftEndDate);
    };

    const clearStartDate = (event) => {
        event.stopPropagation();
        setDraftStartDate(null);
        setDraftEndDate(null);
        setSelectedPreset(CUSTOM_RANGE);
    };

    const clearEndDate = (event) => {
        event.stopPropagation();
        setDraftEndDate(null);
        setSelectedPreset(CUSTOM_RANGE);
    };

    const renderClearAdornment = (value, onClear, existingAdornment) => {
        if (!value) {
            return existingAdornment;
        }

        return (
            <>
                <InputAdornment position="end">
                    <IconButton
                        edge="end"
                        onClick={onClear}
                        onMouseDown={(event) => event.preventDefault()}
                        size="small"
                    >
                        <ClearIcon fontSize="small" />
                    </IconButton>
                </InputAdornment>
                {existingAdornment}
            </>
        );
    };

    const canApplyCustomDateRange = Boolean(
        draftStartDate
        && draftEndDate
        && (
            !appliedStartDate
            || !appliedEndDate
            || !draftStartDate.isSame(appliedStartDate, 'day')
            || !draftEndDate.isSame(appliedEndDate, 'day')
        ),
    );
    const dateRangeFieldGridProps = selectedPreset === CUSTOM_RANGE
        ? { lg: 3, md: 3, sm: 12, xs: 12 }
        : { lg: 4, md: 12, sm: 12, xs: 12 };

    return (
        <ThemeProvider theme={blackDatePickerTheme}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid container spacing={2}>
                    <Grid item {...dateRangeFieldGridProps}>
                        <Input
                            select
                            label="Date Range"
                            value={selectedPreset}
                            onChange={onChangePreset}
                        >
                            {presetOptions.map((preset) => (
                                <MenuItem key={preset.value} value={preset.value}>
                                    {preset.label}
                                </MenuItem>
                            ))}
                        </Input>
                    </Grid>
                    {selectedPreset === CUSTOM_RANGE && (
                        <>
                            <Grid item lg={4} md={4} sm={12} xs={12}>
                                <DatePicker
                                    label={startLabel}
                                    value={draftStartDate}
                                    minDate={minStartDate}
                                    maxDate={maxDate}
                                    onChange={onChangeStartDate}
                                    renderInput={(params) => (
                                        <Input
                                            {...params}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: renderClearAdornment(
                                                    draftStartDate,
                                                    clearStartDate,
                                                    params.InputProps?.endAdornment,
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item lg={4} md={4} sm={12} xs={12}>
                                <DatePicker
                                    label={endLabel}
                                    value={draftEndDate}
                                    disabled={!draftStartDate}
                                    minDate={draftStartDate}
                                    maxDate={maxDate}
                                    onChange={onChangeEndDate}
                                    renderInput={(params) => (
                                        <Input
                                            {...params}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: renderClearAdornment(
                                                    draftEndDate,
                                                    clearEndDate,
                                                    params.InputProps?.endAdornment,
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item lg={1} md={1} sm={12} xs={12} display="flex" alignItems="center">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth={!matchUpLg}
                                    disabled={!canApplyCustomDateRange}
                                    sx={{ textTransform: 'none' }}
                                    onClick={applyCustomDateRange}
                                >
                                    Apply
                                </Button>
                            </Grid>
                        </>
                    )}
                </Grid>
            </LocalizationProvider>
        </ThemeProvider>
    );
}

DateRangeFields.propTypes = {
    endLabel: PropTypes.string,
    initialEndDate: PropTypes.shape({}),
    initialStartDate: PropTypes.shape({}),
    maxDate: PropTypes.shape({}).isRequired,
    minStartDate: PropTypes.shape({}).isRequired,
    onChangeDateRange: PropTypes.func.isRequired,
    startLabel: PropTypes.string,
};

DateRangeFields.defaultProps = {
    endLabel: 'End Date',
    initialEndDate: null,
    initialStartDate: null,
    startLabel: 'Start Date',
};

export default DateRangeFields;
